// © 2026 AttainIP Engineering

/**
 * Proactive session-expiry watcher (RFC-0202 §"Session-Loss UX" §4).
 *
 * Mounted once at the app root. Schedules a silent refresh ~60s before the
 * access token expires, so in the happy path the user never sees a 401 — the
 * session is renewed before any request can fail. In the unhappy path
 * (refresh token also dead) the user is redirected to login with a notice
 * BEFORE they hit a hang in chat or watch the analysis progress freeze.
 *
 * Edge cases handled:
 *  - Laptop sleep: a setTimeout may not fire while suspended, so we also
 *    re-check on visibilitychange / focus and refresh immediately if we're
 *    already inside the 60s window.
 *  - Wi-Fi blip: a `network` failure gets one 10s retry before sign-out, so a
 *    transient connectivity drop 14 minutes into a session doesn't log the
 *    user out (editor feedback).
 *
 * Out of scope: mid-stream SSE expiry. A chat SSE opened with a valid token
 * can keep streaming after the token expires server-side because the
 * connection is already authenticated; the next *request* would 401, but this
 * watcher refreshes before then, so the dominant case is covered.
 */

import { useEffect } from 'react';
import { useAuthStore } from '../store/auth';
import { restoreSessionFromRefreshToken } from './restoreSession';

/** Refresh this many ms before the token's actual expiry. */
const REFRESH_LEAD_MS = 60_000;
/** After a transient network failure, wait this long before the final try. */
const NETWORK_RETRY_MS = 10_000;

export function useSessionExpiryWatcher(): void {
  const expiresAt = useAuthStore((s) => s.expiresAt);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated || !expiresAt) return;

    let retryId: ReturnType<typeof setTimeout> | undefined;

    const redirect = (): void => {
      useAuthStore.getState().logout();
      window.location.href = import.meta.env.BASE_URL + 'login?reason=session-expired';
    };

    // Second-chance attempt after a network blip — any failure now is terminal.
    const tickFinal = async (): Promise<void> => {
      const result = await restoreSessionFromRefreshToken({ force: true });
      if (!result.ok) redirect();
    };

    const tick = async (): Promise<void> => {
      const result = await restoreSessionFromRefreshToken({ force: true });
      if (result.ok) return; // setAuth wrote a new expiresAt → this effect re-runs.
      if (result.reason === 'network') {
        retryId = setTimeout(() => { void tickFinal(); }, NETWORK_RETRY_MS);
        return;
      }
      redirect(); // 'rejected' | 'no-stored-token' — session is dead.
    };

    const delay = Math.max(0, expiresAt - Date.now() - REFRESH_LEAD_MS);
    const id = setTimeout(() => { void tick(); }, delay);

    // Tab resumed from sleep / background — if we're already inside the
    // refresh window, don't wait for the (possibly missed) timer.
    const onResume = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() >= expiresAt - REFRESH_LEAD_MS) void tick();
    };
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);

    return () => {
      clearTimeout(id);
      if (retryId !== undefined) clearTimeout(retryId);
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('focus', onResume);
    };
  }, [expiresAt, isAuthenticated]);
}
